"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CorePivotTable = exports.CorePivotTableAgImpl = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2022 GoodData Corporation
var all_modules_1 = require("@ag-grid-community/all-modules");
var uuid_1 = require("uuid");
var sdk_model_1 = require("@gooddata/sdk-model");
var react_1 = require("@ag-grid-community/react");
var react_2 = tslib_1.__importDefault(require("react"));
var react_intl_1 = require("react-intl");
require("../styles/css/pivotTable.css");
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_ui_theme_provider_1 = require("@gooddata/sdk-ui-theme-provider");
var aggregationsMenuHelper_1 = require("./impl/structure/headers/aggregationsMenuHelper");
var utils_1 = require("./impl/utils");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var noop_1 = tslib_1.__importDefault(require("lodash/noop"));
var debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
var ts_invariant_1 = require("ts-invariant");
var agUtils_1 = require("./impl/base/agUtils");
var gridOptions_1 = require("./impl/gridOptions");
var tableFacadeInitializer_1 = require("./impl/tableFacadeInitializer");
var tableState_1 = require("./tableState");
var columnSizing_1 = require("./impl/resizing/columnSizing");
var DEFAULT_COLUMN_WIDTH = 200;
var WATCHING_TABLE_RENDERED_INTERVAL = 500;
var AGGRID_ON_RESIZE_TIMEOUT = 300;
/**
 * This class implements pivot table using the community version of ag-grid.
 *
 * Bear in mind that this is not a typical, standard React component implementation; the main reason
 * behind that is that while ag-grid comes with a React component the ag-grid itself is not a React component
 * and vast majority of its APIs are non-React as well. You will therefore find that there is a lot of non-react
 * state flying around.
 *
 * Instead of looking at this implementation as a typical React component, look at it like a adapter between
 * React and ag-grid which is used to render data obtained using GD executions.
 *
 * The code in this class is built to reflect the adapter nature of the integration. The responsibility of this
 * component is to correctly handle the component lifecycle and orchestrate integration of React and ag-grid, React
 * and GoodData, React and GoodData and ag-grid.
 *
 * Lifecycle
 * ---------
 *
 * The goal of the table is to render data that it obtains from GD platform by driving an execution. To this end
 * the prop 'execution' contains an instance of Prepared Execution which is all set up and ready to run.
 *
 * Before rendering anything, the code must first drive this prepared execution completion in order to figure out
 * how the actual table should look like header-wise.
 *
 * Once the execution completes successfully code will process the result and the metadata included within
 * to construct table headers for ag-grid and prepare an ag-grid data source that the ag-grid will use to read
 * pages of data from backend. Note: while constructing table headers, the code will also apply manual column
 * sizing settings.
 *
 * With this ready, the component can render the actual ag-grid table. It will create ag-grid options with
 * all the necessary metadata and customizations.
 *
 * After the table is successfully rendered, the code may (depending on props) apply grow-to-width and auto-resizing
 * logic on the table columns. And then finally it will determine and set the sticky row contents.
 *
 * At this point when the table is rendered, the users may interact with it and change sorting or add totals
 * or subtotals. All of this is handled outside of React. These changes are handled in the ag-grid data source
 * implementation. As it discovers that different sorts or totals are needed, it will transform the original
 * prepared execution, add the extra settings and re-drive the execution. Once done, it will update the internal
 * state and ping ag-grid API to re-render the table.
 *
 * In case the client changes props (say modifying the prepared execution) the componentDidUpdate will determine
 * whether the full reinitialization is in order. If so the entire existing ag-grid and all our internal state
 * is thrown out and table is initialized from scratch.
 *
 * Notable sub-components
 * ----------------------
 *
 * All custom logic that we build on top of ag-grid has the entry point in `TableFacade`. The code in this component
 * relies on the facade to drive our custom table logic. This facade further delegates complex pieces of work
 * to other components. The most important are `TableDescriptor` and `ResizedColumnStore` + its friends.
 *
 * The `TableDescriptor` is responsible for figuring out how the table should look like and prepare column
 * descriptors and ag-grid ColDefs.
 *
 * The `ResizedColumnStore` & functions in its vicinity are responsible for implementation of our custom
 * table column resizing logic.
 *
 * Apart from these main components there is also our custom implementation of ag-grid data source - this is responsible
 * for getting correct data and transforming it to form that can be consumed by ag-gird. It is the data source where
 * our code has to figure out whether the sorts or totals have changed and if so update the execution to perform
 * the correct execution.
 *
 * Finally there is the sticky row handling which contains some nasty code & at times works with ag-grid internals
 * to get the job done.
 *
 * Control flow
 * ------------
 *
 * To maintain some kind of sanity in this complex component there are two general rules:
 *
 * 1.  TableMethods or its subtypes are the only way how pivot table component passes down its essential functions
 *     to sub-components.
 *
 * 2.  All table functionality and features MUST be orchestrated from the pivot table component itself.
 *     The facade or other subcomponents merely _do the work_ but they do not make 'high level decisions' of
 *     what the table should be doing.
 *
 * These rules are in place to try and get to 'top-down' control flow.
 *
 *
 * Known flaws
 * -----------
 *
 * -  The initial render & subsequent table column resizing is brittle and includes a async functions, timeouts, intervals
 *    etc.
 *
 *    This can be currently knocked out of balance if during initial table render the data source determines
 *    it needs to transform the execution (to include sorts for instance; this was often the case if AD sent execution
 *    definition with invalid sorts).
 *
 * -  The reinitialization of entire table is too aggressive at the moment. There are two most notable cases:
 *
 *    1.  Client changes drills; this will lead to reinit to correctly mark cells as drillable. Perhaps all we
 *        need it to trigger some kind of ag-grid cell refresh?
 *
 *    2.  Client changes prepared execution that comes in props. Any change means reinit. This is not really needed
 *        if only sorts or totals were added but the shape of the table looks the same.
 *
 * Debugging hints
 * ---------------
 *
 * Nothing renders: check out the problem with resizing & data source interplay.
 *
 *
 * @internal
 */
var CorePivotTableAgImpl = /** @class */ (function (_super) {
    tslib_1.__extends(CorePivotTableAgImpl, _super);
    function CorePivotTableAgImpl(props) {
        var _this = _super.call(this, props) || this;
        //
        // Lifecycle
        //
        /**
         * Starts initialization of table that will show results of the provided prepared execution. If there is
         * already an initialization in progress for the table, this will abandon the previous initialization
         * and start a new one.
         *
         * During the initialization the code drives the execution and obtains the first page of data. Once that
         * is done, the initializer will construct the {@link TableFacade} with all the essential non-react
         * table & data state in it.
         *
         * After the initializer completes this, the table facade and the table itself is ready to render the
         * ag-grid component.
         *
         * @param execution - prepared execution to drive
         */
        _this.initialize = function (execution) {
            _this.internal.abandonInitialization();
            var initializer = new tableFacadeInitializer_1.TableFacadeInitializer(execution, _this.getTableMethods(), _this.props);
            initializer.initialize().then(function (result) {
                if (!result || _this.internal.initializer !== result.initializer) {
                    /*
                     * This particular initialization was abandoned.
                     */
                    return;
                }
                _this.internal.initializer = undefined;
                _this.internal.table = result.table;
                _this.setState({ readyToRender: true });
            });
            return initializer;
        };
        /**
         * Completely re-initializes the table in order to show data for the provided prepared execution. At this point
         * code has determined that the table layout for the other prepared execution differs from what is currently
         * shown and the only reasonable thing to do is to throw everything away and start from scratch.
         *
         * This will reset all React state and non-react state and start table initialization process.
         */
        _this.reinitialize = function (execution) {
            _this.setState({
                readyToRender: false,
                columnTotals: cloneDeep_1.default(sdk_model_1.defTotals(execution.definition, 0)),
                error: undefined,
                desiredHeight: _this.props.config.maxHeight,
                resized: false,
            }, function () {
                _this.internal.destroy();
                _this.internal = new tableState_1.InternalTableState();
                _this.boundAgGridCallbacks = _this.createBoundAgGridCallbacks();
                _this.internal.initializer = _this.initialize(execution);
            });
        };
        //
        // Render
        //
        _this.setContainerRef = function (container) {
            _this.containerRef = container;
            if (_this.containerRef) {
                _this.containerRef.addEventListener("mousedown", _this.onContainerMouseDown);
            }
        };
        //
        // event handlers
        //
        _this.onGridReady = function (event) {
            ts_invariant_1.invariant(_this.internal.table);
            _this.internal.table.finishInitialization(event.api, event.columnApi);
            _this.updateDesiredHeight();
            if (_this.getGroupRows()) {
                _this.internal.table.initializeStickyRow();
            }
            _this.internal.table.setTooltipFields();
        };
        _this.onFirstDataRendered = function (_event) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        ts_invariant_1.invariant(this.internal.table);
                        if (this.internal.firstDataRendered) {
                            // eslint-disable-next-line no-console
                            console.error("onFirstDataRendered called multiple times");
                        }
                        this.internal.firstDataRendered = true;
                        // Since issue here is not resolved, https://github.com/ag-grid/ag-grid/issues/3263,
                        // work-around by using 'setInterval'
                        this.internal.startWatching(this.startWatchingTableRendered, WATCHING_TABLE_RENDERED_INTERVAL);
                        if (!this.isColumnAutoresizeEnabled()) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.autoresizeColumns()];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        if (this.isGrowToFitEnabled()) {
                            this.growToFit();
                        }
                        _a.label = 3;
                    case 3:
                        this.updateStickyRow();
                        return [2 /*return*/];
                }
            });
        }); };
        _this.onModelUpdated = function () {
            _this.updateStickyRow();
        };
        _this.onGridColumnsChanged = function () {
            _this.updateStickyRow();
        };
        _this.onGridSizeChanged = function (gridSizeChangedEvent) {
            if (!_this.internal.firstDataRendered) {
                // ag-grid does emit the grid size changed even before first data gets rendered (i suspect this is
                // to cater for the initial render where it goes from nothing to something that has the headers, and then
                // it starts rendering the data itself)
                //
                // Don't do anything, the resizing will be triggered after the first data is rendered
                return;
            }
            if (!_this.internal.table) {
                return;
            }
            if (_this.internal.table.isResizing()) {
                // don't do anything if the table is already resizing. this copies what we have in v7 line however
                // I think it opens room for racy/timing behavior. if the window is resized _while_ the table is resizing
                // it is likely that it will not respect current window size.
                //
                // keeping it like this for now. if needed, we can enqueue an auto-resize request somewhere and process
                // it after resizing finishes.
                return;
            }
            if (_this.internal.checkAndUpdateLastSize(gridSizeChangedEvent.clientWidth, gridSizeChangedEvent.clientHeight)) {
                _this.autoresizeColumns(true);
            }
        };
        _this.onGridColumnResized = function (columnEvent) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                ts_invariant_1.invariant(this.internal.table);
                if (!columnEvent.finished) {
                    return [2 /*return*/]; // only update the height once the user is done setting the column size
                }
                this.updateDesiredHeight();
                if (agUtils_1.isManualResizing(columnEvent)) {
                    this.internal.table.onManualColumnResize(this.getResizingConfig(), columnEvent.columns);
                }
                return [2 /*return*/];
            });
        }); };
        _this.onSortChanged = function (event) {
            if (!_this.internal.table) {
                // eslint-disable-next-line no-console
                console.warn("changing sorts without prior execution cannot work");
                return;
            }
            var sortItems = _this.internal.table.createSortItems(event.columnApi.getAllColumns());
            // eslint-disable-next-line no-console
            console.debug("onSortChanged", sortItems);
            _this.pushDataGuard({
                properties: {
                    sortItems: sortItems,
                },
            });
        };
        _this.onPinnedRowDataChanged = function (event) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!((event === null || event === void 0 ? void 0 : event.api.getPinnedBottomRowCount()) > 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.autoresizeColumns(true)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        _this.onBodyScroll = function (event) {
            var scrollPosition = {
                top: Math.max(event.top, 0),
                left: event.left,
            };
            _this.updateStickyRowContent(scrollPosition);
        };
        _this.onContainerMouseDown = function (event) {
            if (event.target && agUtils_1.isHeaderResizer(event.target)) {
                event.stopPropagation();
            }
            _this.internal.isMetaOrCtrlKeyPressed = event.metaKey || event.ctrlKey;
            _this.internal.isAltKeyPressed = event.altKey;
        };
        _this.onPageLoaded = function (_dv, newResult) {
            var _a, _b;
            if (!_this.internal.table) {
                return;
            }
            if (newResult) {
                (_b = (_a = _this.props).onExportReady) === null || _b === void 0 ? void 0 : _b.call(_a, _this.internal.table.createExportFunction(_this.props.exportTitle));
            }
            _this.updateStickyRow();
            _this.updateDesiredHeight();
        };
        /**
         * This will be called when user changes sorts or adds totals. This means complete re-execution with
         * new sorts or totals. Loading indicators will be shown instead of all rendered rows thanks to the
         * LoadingRenderer used in all cells of the left-most column.
         *
         * The table must take care to remove the sticky (top-pinned) row - it is treated differently by
         * ag-grid and will be literally sticking there on its own with the loading indicators.
         *
         * Once transformation finishes - indicated by call to onPageLoaded, table can re-instance the sticky row.
         *
         * @param _newExecution - the new execution which is being run and will be used to populate the table
         */
        _this.onExecutionTransformed = function (_newExecution) {
            if (!_this.internal.table) {
                return;
            }
            _this.internal.table.clearStickyRow();
        };
        _this.onMenuAggregationClick = function (menuAggregationClickConfig) {
            var newColumnTotals = aggregationsMenuHelper_1.getUpdatedColumnTotals(_this.getColumnTotals(), menuAggregationClickConfig);
            _this.pushDataGuard({
                properties: {
                    totals: newColumnTotals,
                },
            });
            _this.setState({ columnTotals: newColumnTotals }, function () {
                var _a;
                (_a = _this.internal.table) === null || _a === void 0 ? void 0 : _a.refreshData();
            });
        };
        _this.onLoadingChanged = function (loadingState) {
            var onLoadingChanged = _this.props.onLoadingChanged;
            if (onLoadingChanged) {
                onLoadingChanged(loadingState);
            }
        };
        _this.onError = function (error, execution) {
            var _a, _b;
            if (execution === void 0) { execution = _this.props.execution; }
            var onExportReady = _this.props.onExportReady;
            if (_this.props.execution.fingerprint() === execution.fingerprint()) {
                _this.setState({ error: error.getMessage() });
                // update loading state when an error occurs
                _this.onLoadingChanged({ isLoading: false });
                onExportReady(sdk_ui_1.createExportErrorFunction(error));
                (_b = (_a = _this.props).onError) === null || _b === void 0 ? void 0 : _b.call(_a, error);
            }
        };
        //
        // Table resizing
        //
        _this.growToFit = function () {
            ts_invariant_1.invariant(_this.internal.table);
            if (!_this.isGrowToFitEnabled()) {
                return;
            }
            _this.internal.table.growToFit(_this.getResizingConfig());
            if (!_this.state.resized && !_this.internal.table.isResizing()) {
                _this.setState({
                    resized: true,
                });
            }
        };
        _this.autoresizeColumns = function (force) {
            if (force === void 0) { force = false; }
            return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var didResize;
                var _a;
                return tslib_1.__generator(this, function (_b) {
                    switch (_b.label) {
                        case 0:
                            if (this.state.resized && !force) {
                                return [2 /*return*/];
                            }
                            return [4 /*yield*/, ((_a = this.internal.table) === null || _a === void 0 ? void 0 : _a.autoresizeColumns(this.getResizingConfig(), force))];
                        case 1:
                            didResize = _b.sent();
                            if (didResize && !this.state.resized) {
                                this.setState({
                                    resized: true,
                                });
                            }
                            return [2 /*return*/];
                    }
                });
            });
        };
        _this.shouldAutoResizeColumns = function () {
            var columnAutoresize = _this.isColumnAutoresizeEnabled();
            var growToFit = _this.isGrowToFitEnabled();
            return columnAutoresize || growToFit;
        };
        _this.startWatchingTableRendered = function () {
            if (!_this.internal.table) {
                return;
            }
            var missingContainerRef = !_this.containerRef; // table having no data will be unmounted, it causes ref null
            var isTableRendered = _this.shouldAutoResizeColumns()
                ? _this.state.resized
                : _this.internal.table.isPivotTableReady();
            var shouldCallAutoresizeColumns = _this.internal.table.isPivotTableReady() &&
                !_this.state.resized &&
                !_this.internal.table.isResizing();
            if (_this.shouldAutoResizeColumns() && shouldCallAutoresizeColumns) {
                _this.autoresizeColumns();
            }
            if (missingContainerRef || isTableRendered) {
                _this.stopWatchingTableRendered();
            }
        };
        _this.stopWatchingTableRendered = function () {
            _this.internal.stopWatching();
            _this.props.afterRender();
        };
        //
        // Sticky row handling
        //
        _this.isStickyRowAvailable = function () {
            ts_invariant_1.invariant(_this.internal.table);
            return Boolean(_this.getGroupRows() && _this.internal.table.stickyRowExists());
        };
        _this.updateStickyRow = function () {
            if (!_this.internal.table) {
                return;
            }
            if (_this.isStickyRowAvailable()) {
                _this.internal.table.updateStickyRowPosition();
                var scrollPosition = tslib_1.__assign({}, _this.internal.lastScrollPosition);
                _this.internal.lastScrollPosition = {
                    top: 0,
                    left: 0,
                };
                _this.updateStickyRowContent(scrollPosition);
            }
        };
        _this.updateStickyRowContent = function (scrollPosition) {
            ts_invariant_1.invariant(_this.internal.table);
            if (_this.isStickyRowAvailable()) {
                _this.internal.table.updateStickyRowContent({
                    scrollPosition: scrollPosition,
                    lastScrollPosition: _this.internal.lastScrollPosition,
                });
            }
            _this.internal.lastScrollPosition = tslib_1.__assign({}, scrollPosition);
        };
        //
        // Desired height updating
        //
        _this.getScrollBarPadding = function () {
            var _a;
            if (!((_a = _this.internal.table) === null || _a === void 0 ? void 0 : _a.isFullyInitialized())) {
                return 0;
            }
            if (!_this.containerRef) {
                return 0;
            }
            // check for scrollbar presence
            return agUtils_1.scrollBarExists(_this.containerRef) ? utils_1.getScrollbarWidth() : 0;
        };
        _this.calculateDesiredHeight = function () {
            var _a, _b;
            var maxHeight = _this.props.config.maxHeight;
            if (!maxHeight) {
                return;
            }
            var bodyHeight = (_b = (_a = _this.internal.table) === null || _a === void 0 ? void 0 : _a.getTotalBodyHeight()) !== null && _b !== void 0 ? _b : 0;
            var totalHeight = bodyHeight + _this.getScrollBarPadding();
            return Math.min(totalHeight, maxHeight);
        };
        _this.updateDesiredHeight = function () {
            if (!_this.internal.table) {
                return;
            }
            var desiredHeight = _this.calculateDesiredHeight();
            /*
             * For some mysterious reasons, there sometimes is exactly 2px discrepancy between the current height
             * and the maxHeight coming from the config. This 2px seems to be unrelated to any CSS property (border,
             * padding, etc.) not even the leeway variable in getTotalBodyHeight.
             * In these cases there is a positive feedback loop between the maxHeight and the config:
             *
             * increase in desiredHeight -> increase in config.maxHeight -> increase in desiredHeight -> ...
             *
             * This causes the table to grow in height in 2px increments until it reaches its full size - then
             * the resizing stops as bodyHeight of the table gets smaller than the maxHeight and "wins"
             * in calculateDesiredHeight)...
             *
             * So we ignore changes smaller than those 2px to break the loop as it is quite unlikely that such a small
             * change would be legitimate (and if it is, a mismatch of 2px should not have practical consequences).
             *
             * Ideally, this maxHeight would not be needed at all (if I remove it altogether, the problem goes away),
             * however, it is necessary for ONE-4322 (there seems to be no native way of doing this in ag-grid itself).
             */
            var HEIGHT_CHANGE_TOLERANCE = 2;
            if (_this.state.desiredHeight === undefined ||
                (desiredHeight !== undefined &&
                    Math.abs(_this.state.desiredHeight - desiredHeight) > HEIGHT_CHANGE_TOLERANCE)) {
                _this.setState({ desiredHeight: desiredHeight });
            }
        };
        //
        // Table configuration accessors
        //
        _this.getColumnTotals = function () {
            return _this.state.columnTotals;
        };
        _this.getExecutionDefinition = function () {
            return _this.props.execution.definition;
        };
        _this.getGroupRows = function () {
            var _a, _b;
            return (_b = (_a = _this.props.config) === null || _a === void 0 ? void 0 : _a.groupRows) !== null && _b !== void 0 ? _b : true;
        };
        _this.getMenuConfig = function () {
            var _a, _b;
            return (_b = (_a = _this.props.config) === null || _a === void 0 ? void 0 : _a.menu) !== null && _b !== void 0 ? _b : {};
        };
        _this.getDefaultWidth = function () {
            return DEFAULT_COLUMN_WIDTH;
        };
        _this.isColumnAutoresizeEnabled = function () {
            var defaultWidth = _this.getDefaultWidthFromProps(_this.props);
            return columnSizing_1.isColumnAutoresizeEnabled(defaultWidth);
        };
        _this.isGrowToFitEnabled = function (props) {
            var _a, _b;
            if (props === void 0) { props = _this.props; }
            return ((_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.columnSizing) === null || _b === void 0 ? void 0 : _b.growToFit) === true;
        };
        _this.getColumnWidths = function (props) {
            var _a;
            return (_a = props.config.columnSizing) === null || _a === void 0 ? void 0 : _a.columnWidths;
        };
        _this.hasColumnWidths = function () {
            return !!_this.getColumnWidths(_this.props);
        };
        _this.getDefaultWidthFromProps = function (props) {
            var _a, _b, _c;
            return (_c = (_b = (_a = props.config) === null || _a === void 0 ? void 0 : _a.columnSizing) === null || _b === void 0 ? void 0 : _b.defaultWidth) !== null && _c !== void 0 ? _c : "unset";
        };
        /**
         * All pushData calls done by the table must go through this guard.
         *
         * TODO: The guard should ensure a 'disconnect' between push data handling and the calling function processing.
         *  When the pushData is handled by the application, it MAY (and in our case it DOES) trigger processing that
         *  lands back in the table. This opens additional set of invariants to check / be prepared for in order to
         *  optimize the renders and re-renders.
         */
        _this.pushDataGuard = function (data) {
            var _a, _b;
            (_b = (_a = _this.props).pushData) === null || _b === void 0 ? void 0 : _b.call(_a, data);
            /*
             * TODO: Switch to this on in FET-715.
            setTimeout(() => {
                this.props.pushData?.(data);
            }, 0);
             */
        };
        /**
         * Wraps the provided callback function with a guard that checks whether the current table state is the same
         * as the state snapshot at the time of callback creation. If the state differs, the wrapped function WILL NOT
         * be called.
         *
         * @param callback - function to wrap with state guard
         */
        // eslint-disable-next-line @typescript-eslint/ban-types
        _this.stateBoundCallback = function (callback) {
            var forInternalState = _this.internal;
            return (function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (_this.internal !== forInternalState) {
                    return;
                }
                return callback.apply(void 0, args);
            });
        };
        /**
         * All callback functions that the table passes to ag-grid must be bound to the current internal state of the table. The
         * callback functions MUST be noop if the internal state at the time of call is different from the internal state
         * at the time of creation.
         *
         * This is essential to prevent errors stemming for racy behavior triggered by ag-grid. ag-grid often triggers
         * event callbacks using setTimeout(). It can happen, that once the event is actually processed the ag-grid table
         * which caused it is unmounted. Doing anything with the unmounted table's gridApi leads to errors.
         *
         * Without this, table may trigger ag-grid errors such as this:
         *
         * https://github.com/ag-grid/ag-grid/issues/3457
         *
         * or this:
         *
         * https://github.com/ag-grid/ag-grid/issues/3334
         */
        _this.createBoundAgGridCallbacks = function () {
            var debouncedGridSizeChanged = debounce_1.default(_this.stateBoundCallback(_this.onGridSizeChanged), AGGRID_ON_RESIZE_TIMEOUT);
            return {
                onGridReady: _this.stateBoundCallback(_this.onGridReady),
                onFirstDataRendered: _this.stateBoundCallback(_this.onFirstDataRendered),
                onBodyScroll: _this.stateBoundCallback(_this.onBodyScroll),
                onModelUpdated: _this.stateBoundCallback(_this.onModelUpdated),
                onGridColumnsChanged: _this.stateBoundCallback(_this.onGridColumnsChanged),
                onGridColumnResized: _this.stateBoundCallback(_this.onGridColumnResized),
                onSortChanged: _this.stateBoundCallback(_this.onSortChanged),
                onGridSizeChanged: debouncedGridSizeChanged,
                onPinnedRowDataChanged: _this.stateBoundCallback(_this.onPinnedRowDataChanged),
            };
        };
        _this.getTableMethods = function () {
            var _a;
            return tslib_1.__assign({ hasColumnWidths: _this.hasColumnWidths(), getExecutionDefinition: _this.getExecutionDefinition, getMenuConfig: _this.getMenuConfig, getGroupRows: _this.getGroupRows, getColumnTotals: _this.getColumnTotals, getResizingConfig: _this.getResizingConfig, onLoadingChanged: _this.onLoadingChanged, onError: _this.onError, onExportReady: (_a = _this.props.onExportReady) !== null && _a !== void 0 ? _a : noop_1.default, pushData: _this.pushDataGuard, onPageLoaded: _this.onPageLoaded, onExecutionTransformed: _this.onExecutionTransformed, onMenuAggregationClick: _this.onMenuAggregationClick }, _this.boundAgGridCallbacks);
        };
        _this.getResizingConfig = function () {
            var _a, _b, _c, _d, _e;
            return {
                defaultWidth: _this.getDefaultWidth(),
                growToFit: _this.isGrowToFitEnabled(),
                columnAutoresizeOption: _this.getDefaultWidthFromProps(_this.props),
                widths: _this.getColumnWidths(_this.props),
                isAltKeyPressed: _this.internal.isAltKeyPressed,
                isMetaOrCtrlKeyPressed: _this.internal.isMetaOrCtrlKeyPressed,
                // use clientWidth of the viewport container to accommodate for vertical scrollbars if needed
                clientWidth: (_c = (_b = (_a = _this.containerRef) === null || _a === void 0 ? void 0 : _a.getElementsByClassName("ag-body-viewport")[0]) === null || _b === void 0 ? void 0 : _b.clientWidth) !== null && _c !== void 0 ? _c : 0,
                containerRef: _this.containerRef,
                separators: (_e = (_d = _this.props) === null || _d === void 0 ? void 0 : _d.config) === null || _e === void 0 ? void 0 : _e.separators,
                onColumnResized: _this.props.onColumnResized,
            };
        };
        var execution = props.execution, config = props.config;
        _this.state = {
            readyToRender: false,
            columnTotals: cloneDeep_1.default(sdk_model_1.defTotals(execution.definition, 0)),
            desiredHeight: config.maxHeight,
            resized: false,
        };
        _this.errorMap = sdk_ui_1.newErrorMapping(props.intl);
        _this.internal = new tableState_1.InternalTableState();
        _this.boundAgGridCallbacks = _this.createBoundAgGridCallbacks();
        _this.pivotTableId = uuid_1.v4().replace(/-/g, "");
        _this.onLoadingChanged = _this.onLoadingChanged.bind(_this);
        return _this;
    }
    CorePivotTableAgImpl.prototype.componentDidMount = function () {
        this.alertParentWrapperMissingHeight();
        this.internal.initializer = this.initialize(this.props.execution);
    };
    CorePivotTableAgImpl.prototype.componentWillUnmount = function () {
        if (this.containerRef) {
            this.containerRef.removeEventListener("mousedown", this.onContainerMouseDown);
        }
        // this ensures any events emitted during the async initialization will be sunk. they are no longer needed.
        this.internal.destroy();
    };
    CorePivotTableAgImpl.prototype.componentDidUpdate = function (prevProps) {
        var _a, _b, _c, _d, _e;
        if (this.isReinitNeeded(prevProps)) {
            /*
             * This triggers when execution changes (new measures / attributes). In that case,
             * a complete re-init of the table is in order.
             *
             * Everything is thrown out of the window including all the ag-grid data sources and instances and
             * a completely new table will be initialized to the current props.
             *
             * Note: compared to v7 version of the table, this only happens if someone actually changes the
             * execution-related props of the table. This branch will not hit any other time.
             */
            // eslint-disable-next-line no-console
            console.debug("triggering reinit", this.props.execution.definition, prevProps.execution.definition);
            this.reinitialize(this.props.execution);
        }
        else {
            /*
             * When in this branch, the ag-grid instance is up and running and is already showing some data and
             * it _should_ be possible to normally use the ag-grid APIs.
             *
             * The currentResult and visibleData _will_ be available at this point because the component is definitely
             * after a successful execution and initialization.
             */
            if (this.shouldRefreshHeader(this.props, prevProps)) {
                (_a = this.internal.table) === null || _a === void 0 ? void 0 : _a.refreshHeader();
            }
            if (this.isGrowToFitEnabled() && !this.isGrowToFitEnabled(prevProps)) {
                this.growToFit();
            }
            var prevColumnWidths = this.getColumnWidths(prevProps);
            var columnWidths = this.getColumnWidths(this.props);
            if (!isEqual_1.default(prevColumnWidths, columnWidths)) {
                (_b = this.internal.table) === null || _b === void 0 ? void 0 : _b.applyColumnSizes(this.getResizingConfig());
            }
            if (((_c = this.props.config) === null || _c === void 0 ? void 0 : _c.maxHeight) &&
                !isEqual_1.default((_d = this.props.config) === null || _d === void 0 ? void 0 : _d.maxHeight, (_e = prevProps.config) === null || _e === void 0 ? void 0 : _e.maxHeight)) {
                this.updateDesiredHeight();
            }
        }
    };
    /**
     * Tests whether reinitialization of ag-grid is needed after the update. Currently there are two
     * conditions:
     *
     * - drilling has changed
     *
     * OR
     *
     * - prepared execution has changed AND the new prep execution definition does not match currently shown
     *   data.
     */
    CorePivotTableAgImpl.prototype.isReinitNeeded = function (prevProps) {
        var drillingIsSame = isEqual_1.default(prevProps.drillableItems, this.props.drillableItems);
        if (!drillingIsSame) {
            // eslint-disable-next-line no-console
            console.debug("drilling is different", prevProps.drillableItems, this.props.drillableItems);
            return true;
        }
        if (!this.internal.table) {
            // Table is not yet fully initialized. See if the initialization is in progress. If so, see if
            // the init is for same execution or not. Otherwise fall back to compare props vs props.
            if (this.internal.initializer) {
                var initializeForSameExec = this.internal.initializer.isSameExecution(this.props.execution);
                if (!initializeForSameExec) {
                    // eslint-disable-next-line no-console
                    console.debug("initializer for different execution", this.props.execution, prevProps.execution);
                }
                return !initializeForSameExec;
            }
            else {
                var prepExecutionSame = this.props.execution.fingerprint() === prevProps.execution.fingerprint();
                if (!prepExecutionSame) {
                    // eslint-disable-next-line no-console
                    console.debug("have to reinit table", this.props.execution, prevProps.execution);
                }
                return !prepExecutionSame;
            }
        }
        return !this.internal.table.isMatchingExecution(this.props.execution);
    };
    /**
     * Tests whether ag-grid's refreshHeader should be called. At the moment this is necessary when user
     * turns on/off the aggregation menus through the props. The menus happen to appear in the table column headers
     * so the refresh is essential to show/hide them.
     *
     * @param props - current table props
     * @param prevProps - previous table props
     * @internal
     */
    CorePivotTableAgImpl.prototype.shouldRefreshHeader = function (props, prevProps) {
        var propsRequiringAgGridRerender = [
            function (props) { var _a; return (_a = props === null || props === void 0 ? void 0 : props.config) === null || _a === void 0 ? void 0 : _a.menu; },
        ];
        return propsRequiringAgGridRerender.some(function (propGetter) { return !isEqual_1.default(propGetter(props), propGetter(prevProps)); });
    };
    /**
     * Checks DOM if height has been set on PivotTable parent wrapper. If height has not been set,
     * PivotTable will not be rendered correctly. Therefore, we need to inform the user at least on the console
     * on what is happening.
     *
     * @internal
     */
    CorePivotTableAgImpl.prototype.alertParentWrapperMissingHeight = function () {
        var _a, _b;
        var parentWrapper = (_a = document.getElementById(this.pivotTableId)) === null || _a === void 0 ? void 0 : _a.parentElement;
        var parentHeight = (_b = parentWrapper === null || parentWrapper === void 0 ? void 0 : parentWrapper.offsetHeight) !== null && _b !== void 0 ? _b : 0;
        if (parentHeight < 20) {
            // eslint-disable-next-line no-console
            console.warn("The wrapper height of the pivot table has not been set or is suspiciously small. This might cause pivot table rendering issues. If so, please set an appropriate height for the wrapper. Use document.getElementById(\"" + this.pivotTableId + "\") to find the PivotTable element in the DOM, which will help you to identify its wrapper.");
        }
    };
    CorePivotTableAgImpl.prototype.renderLoading = function () {
        var _a, _b, _c, _d, _e;
        var _f = this.props, LoadingComponent = _f.LoadingComponent, theme = _f.theme;
        var color = (_e = (_b = (_a = theme === null || theme === void 0 ? void 0 : theme.table) === null || _a === void 0 ? void 0 : _a.loadingIconColor) !== null && _b !== void 0 ? _b : (_d = (_c = theme === null || theme === void 0 ? void 0 : theme.palette) === null || _c === void 0 ? void 0 : _c.complementary) === null || _d === void 0 ? void 0 : _d.c6) !== null && _e !== void 0 ? _e : undefined;
        return (react_2.default.createElement("div", { className: "s-loading gd-table-loading" }, LoadingComponent ? react_2.default.createElement(LoadingComponent, { color: color }) : null));
    };
    CorePivotTableAgImpl.prototype.render = function () {
        var ErrorComponent = this.props.ErrorComponent;
        var _a = this.state, desiredHeight = _a.desiredHeight, error = _a.error;
        if (error) {
            var errorProps = this.errorMap[Object.prototype.hasOwnProperty.call(this.errorMap, error)
                ? error
                : sdk_ui_1.ErrorCodes.UNKNOWN_ERROR];
            return ErrorComponent ? react_2.default.createElement(ErrorComponent, tslib_1.__assign({ code: error }, errorProps)) : null;
        }
        var style = {
            height: desiredHeight || "100%",
            position: "relative",
            overflow: "hidden",
        };
        if (!this.state.readyToRender) {
            return (react_2.default.createElement("div", { className: "gd-table-component", style: style, id: this.pivotTableId }, this.renderLoading()));
        }
        // when table is ready, then the table facade must be set. if this bombs then there is a bug
        // in the initialization logic
        ts_invariant_1.invariant(this.internal.table);
        if (!this.internal.gridOptions) {
            this.internal.gridOptions = gridOptions_1.createGridOptions(this.internal.table, this.getTableMethods(), this.props);
        }
        /*
         * Show loading overlay until all the resizing is done. This is because the various resizing operations
         * have to happen asynchronously - they must wait until ag-grid fires onFirstDataRendered, before our code
         * can start reliably interfacing with the autosizing features.
         */
        var shouldRenderLoadingOverlay = (this.isColumnAutoresizeEnabled() || this.isGrowToFitEnabled()) && !this.state.resized;
        return (react_2.default.createElement("div", { className: "gd-table-component", style: style, id: this.pivotTableId },
            react_2.default.createElement("div", { className: "gd-table ag-theme-balham s-pivot-table", style: style, ref: this.setContainerRef },
                react_2.default.createElement(react_1.AgGridReact, tslib_1.__assign({}, this.internal.gridOptions, { modules: all_modules_1.AllCommunityModules })),
                shouldRenderLoadingOverlay ? this.renderLoading() : null)));
    };
    CorePivotTableAgImpl.defaultProps = {
        locale: "en-US",
        drillableItems: [],
        afterRender: noop_1.default,
        pushData: noop_1.default,
        onExportReady: noop_1.default,
        onLoadingChanged: noop_1.default,
        onError: noop_1.default,
        onDrill: function () { return true; },
        ErrorComponent: sdk_ui_1.ErrorComponent,
        LoadingComponent: sdk_ui_1.LoadingComponent,
        pageSize: 100,
        config: {},
        onColumnResized: noop_1.default,
    };
    return CorePivotTableAgImpl;
}(react_2.default.Component));
exports.CorePivotTableAgImpl = CorePivotTableAgImpl;
var CorePivotTableWithIntl = react_intl_1.injectIntl(sdk_ui_theme_provider_1.withTheme(CorePivotTableAgImpl));
/**
 * @internal
 */
exports.CorePivotTable = function (props) { return (react_2.default.createElement(sdk_ui_theme_provider_1.ThemeContextProvider, { theme: props.theme || {}, themeIsLoading: false },
    react_2.default.createElement(sdk_ui_1.IntlWrapper, { locale: props.locale },
        react_2.default.createElement(CorePivotTableWithIntl, tslib_1.__assign({}, props))))); };
//# sourceMappingURL=CorePivotTable.js.map